// AccidentStatsRegister — client-level Accident Statistics / RIDDOR annual
// register (Year Ending → Frequency Rate), reusable for any client. Historic
// rows (e.g. Saltash 2021-2025) are preserved exactly as supplied and are
// never auto-recalculated; new years use the printed HSE formulas as a
// starting point that stays fully editable.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Loader2, Printer, X, Save, Trash2, Calculator, History, RefreshCw, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, filenameFromResponse } from "@/lib/api";
import Pill from "@/components/Pill";

const BRAND = "var(--brand-accent, #C75A1B)";
const inputCls = "w-full h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm focus:outline-none focus:border-[var(--brand-accent,_#C75A1B)]";

const COLUMNS = [
    ["year_ending", "Year Ending"], ["employees", "Employees"], ["fatal", "Fatal"],
    ["reportable_injuries", "Reportable Injuries"], ["non_reportable", "Non-Reportable"],
    ["dangerous_occurrences", "Dangerous Occurrences"], ["reportable_ill_health", "Reportable Ill Health"],
    ["near_misses", "Near Misses"], ["incidence_rate", "Incidence Rate"], ["frequency_rate", "Frequency Rate"],
];

function fmt(v, decimals) {
    if (v === null || v === undefined || v === "") return "—";
    const n = Number(v);
    if (Number.isNaN(n)) return String(v);
    if (decimals !== undefined) return n.toLocaleString("en-GB", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    return Number.isInteger(n) ? n.toLocaleString("en-GB") : n.toLocaleString("en-GB", { maximumFractionDigits: 2 });
}

export default function AccidentStatsRegister({ clients, isPortal, portalClientId, canEdit }) {
    const [clientId, setClientId] = useState(isPortal ? portalClientId : "");
    const [rows, setRows] = useState([]);
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(false);
    const [dialog, setDialog] = useState(null); // null | "new" | row
    const [editingSettings, setEditingSettings] = useState(false);
    const [settingsForm, setSettingsForm] = useState(null);
    const [pdfBusy, setPdfBusy] = useState(false);
    const [includeCurrentYear, setIncludeCurrentYear] = useState(true);
    const currentYear = new Date().getFullYear();
    const hasCurrentYearRow = rows.some((r) => r.year_ending === currentYear);

    useEffect(() => { if (isPortal) setClientId(portalClientId); }, [isPortal, portalClientId]);

    const refresh = useCallback(() => {
        if (!clientId) { setRows([]); setSettings(null); return; }
        setLoading(true);
        Promise.all([
            api.get("/accident-stats", { params: { client_id: clientId } }),
            api.get("/accident-stats/settings", { params: { client_id: clientId } }),
        ]).then(([r1, r2]) => { setRows(r1.data); setSettings(r2.data); })
            .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Could not load register"))
            .finally(() => setLoading(false));
    }, [clientId]);
    useEffect(() => { refresh(); }, [refresh]);

    const totals = useMemo(() => {
        const sum = (k) => rows.reduce((a, r) => a + (Number(r[k]) || 0), 0);
        return {
            employees: sum("employees"), fatal: sum("fatal"), reportable_injuries: sum("reportable_injuries"),
            non_reportable: sum("non_reportable"), dangerous_occurrences: sum("dangerous_occurrences"),
            reportable_ill_health: sum("reportable_ill_health"), near_misses: sum("near_misses"),
            // Frequency Rate total matches the source report's own math (sum of yearly rates) —
            // unlike Incidence Rate, this one IS a simple, reliable sum.
            frequency_rate: sum("frequency_rate"),
        };
    }, [rows]);

    const [editingTotalsIR, setEditingTotalsIR] = useState(false);
    const [totalsIRDraft, setTotalsIRDraft] = useState("");

    async function saveTotalsIR() {
        try {
            const val = totalsIRDraft === "" ? null : parseFloat(totalsIRDraft);
            const { data } = await api.patch("/accident-stats/settings", { client_id: clientId, totals_incidence_rate: val });
            setSettings(data);
            setEditingTotalsIR(false);
            toast.success("Cumulative Incidence Rate total updated");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    }

    async function saveSettings() {
        try {
            const { data } = await api.patch("/accident-stats/settings", { ...settingsForm, client_id: clientId });
            setSettings(data); setEditingSettings(false);
            toast.success("Benchmark & totals updated");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    }

    async function downloadPdf() {
        setPdfBusy(true);
        try {
            const res = await api.get("/accident-stats/pdf", {
                params: { client_id: clientId, include_current_year: includeCurrentYear }, responseType: "blob",
            });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url; a.download = filenameFromResponse(res, "accident-statistics.pdf");
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "PDF generation failed"); }
        setPdfBusy(false);
    }

    async function deleteRow(row) {
        if (!window.confirm(`Delete the ${row.year_ending} record?`)) return;
        try {
            await api.delete(`/accident-stats/${row.id}`);
            toast.success("Record deleted");
            refresh();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
    }

    return (
        <div data-testid="accident-stats-register" className="space-y-4">
            <p className="text-[12.5px] text-[#6F6A64]">
                Annual RIDDOR accident statistics — Year Ending through Frequency Rate — kept client by client.
                Historic figures are preserved exactly as supplied; new years use the printed HSE formulas as a starting point.
            </p>
            <div className="flex items-end justify-between gap-3 flex-wrap">
                <div className="flex items-end gap-2 flex-wrap">
                    {!isPortal && (
                        <div className="flex flex-col gap-1">
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-[#9A968F]">Client</span>
                            <select data-testid="accident-stats-client-select" value={clientId}
                                onChange={(e) => setClientId(e.target.value)}
                                className="h-9 px-2.5 rounded-lg border text-[12.5px] bg-white" style={{ borderColor: "#E5E0DA" }}>
                                <option value="">— Select client —</option>
                                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </div>
                    )}
                </div>
                {clientId && (
                    <div className="flex items-center gap-3">
                        {canEdit && (
                            <button data-testid="accident-stats-new-row" onClick={() => setDialog("new")}
                                className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-2 rounded-lg text-white"
                                style={{ background: BRAND }}>
                                <Plus size={14} /> Add year
                            </button>
                        )}
                        {hasCurrentYearRow && (
                            <label className="flex items-center gap-1.5 text-[12px] text-[#6F6A64] cursor-pointer">
                                <input type="checkbox" data-testid="accident-stats-include-current-year"
                                    checked={includeCurrentYear} onChange={(e) => setIncludeCurrentYear(e.target.checked)} />
                                Include {currentYear} (in progress) in PDF
                            </label>
                        )}
                        <button data-testid="accident-stats-download-pdf" onClick={downloadPdf} disabled={pdfBusy || rows.length === 0}
                            className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-2 rounded-lg disabled:opacity-50"
                            style={{ border: "1px solid #E5E0DA", color: "#1F1F1F" }}>
                            {pdfBusy ? <Loader2 size={14} className="animate-spin" /> : <Printer size={14} />} Print / Export PDF
                        </button>
                    </div>
                )}
            </div>

            {!clientId ? (
                <div className="text-center text-[13px] text-[#9A968F] py-16 border border-dashed rounded-xl" style={{ borderColor: "#E5E0DA" }}>
                    Select a client to view or maintain their accident statistics register.
                </div>
            ) : loading ? (
                <div className="text-center py-16"><Loader2 className="animate-spin inline" size={22} /></div>
            ) : rows.length === 0 ? (
                <div className="text-center text-[13px] text-[#9A968F] py-16 border border-dashed rounded-xl" style={{ borderColor: "#E5E0DA" }}>
                    No accident statistics recorded yet for this client.
                    {canEdit && (
                        <div className="mt-3">
                            <button data-testid="accident-stats-add-first-year" onClick={() => setDialog("new")}
                                className="text-[12.5px] font-semibold underline" style={{ color: BRAND }}>
                                Add the first year
                            </button>
                        </div>
                    )}
                </div>
            ) : (
                <div className="bg-white border rounded-[13px] overflow-x-auto shadow-brand-sm" style={{ borderColor: "#E5E0DA" }}>
                    <table className="w-full text-[12.5px]" data-testid="accident-stats-table">
                        <thead><tr className="text-left" style={{ background: "#514E4B" }}>
                            {COLUMNS.map(([k, label]) => (
                                <th key={k} className="px-3 py-2.5 font-semibold text-white whitespace-nowrap">{label}</th>
                            ))}
                            <th className="px-3 py-2.5"></th>
                        </tr></thead>
                        <tbody>
                            {rows.map((r, i) => (
                                <tr key={r.id} data-testid={`accident-stats-row-${r.year_ending}`}
                                    className={`border-b last:border-b-0 ${i % 2 ? "bg-[#F5F3F0]" : ""} ${canEdit ? "cursor-pointer hover:bg-[#F0EDE8]" : ""}`}
                                    style={{ borderColor: "#F0EDE8" }} onClick={() => canEdit && setDialog(r)}>
                                    <td className="px-3 py-2.5 font-semibold text-[#1F1F1F] whitespace-nowrap">
                                        {r.year_ending}{" "}
                                        {r.is_historic && (
                                            <Pill tone="neutral" className="ml-1" data-testid={`accident-stats-historic-${r.year_ending}`}>
                                                <History size={9} /> Historic
                                            </Pill>
                                        )}
                                    </td>
                                    <td className="px-3 py-2.5">{fmt(r.employees)}</td>
                                    <td className="px-3 py-2.5">{fmt(r.fatal)}</td>
                                    <td className="px-3 py-2.5">{fmt(r.reportable_injuries)}</td>
                                    <td className="px-3 py-2.5">{fmt(r.non_reportable)}</td>
                                    <td className="px-3 py-2.5">{fmt(r.dangerous_occurrences)}</td>
                                    <td className="px-3 py-2.5">{fmt(r.reportable_ill_health)}</td>
                                    <td className="px-3 py-2.5">{fmt(r.near_misses)}</td>
                                    <td className="px-3 py-2.5">{fmt(r.incidence_rate)}</td>
                                    <td className="px-3 py-2.5">{fmt(r.frequency_rate, 2)}</td>
                                    <td className="px-3 py-2.5 text-right">
                                        {canEdit && (
                                            <button data-testid={`accident-stats-delete-${r.year_ending}`}
                                                onClick={(e) => { e.stopPropagation(); deleteRow(r); }}
                                                className="text-[#9A968F] hover:text-red-600"><Trash2 size={13} /></button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            <tr className="font-bold" style={{ background: "#E7E4DF" }}>
                                <td className="px-3 py-2.5">Totals</td>
                                <td className="px-3 py-2.5">{fmt(totals.employees)}</td>
                                <td className="px-3 py-2.5">{fmt(totals.fatal)}</td>
                                <td className="px-3 py-2.5">{fmt(totals.reportable_injuries)}</td>
                                <td className="px-3 py-2.5">{fmt(totals.non_reportable)}</td>
                                <td className="px-3 py-2.5">{fmt(totals.dangerous_occurrences)}</td>
                                <td className="px-3 py-2.5">{fmt(totals.reportable_ill_health)}</td>
                                <td className="px-3 py-2.5">{fmt(totals.near_misses)}</td>
                                <td className="px-3 py-2.5" data-testid="accident-stats-totals-ir">
                                    {editingTotalsIR ? (
                                        <div className="flex items-center gap-1">
                                            <input autoFocus type="number" step="0.01" value={totalsIRDraft}
                                                onChange={(e) => setTotalsIRDraft(e.target.value)}
                                                onKeyDown={(e) => { if (e.key === "Enter") saveTotalsIR(); if (e.key === "Escape") setEditingTotalsIR(false); }}
                                                className="w-20 h-7 px-1.5 rounded border text-[12px]" style={{ borderColor: "#E5E0DA" }} />
                                            <button onClick={saveTotalsIR} data-testid="accident-stats-save-totals-ir" title="Save">
                                                <Save size={13} />
                                            </button>
                                        </div>
                                    ) : canEdit ? (
                                        <button type="button" data-testid="accident-stats-edit-totals-ir"
                                            onClick={() => { setTotalsIRDraft(settings?.totals_incidence_rate ?? ""); setEditingTotalsIR(true); }}
                                            className="inline-flex items-center gap-1 hover:underline" title="Cumulative Incidence Rate isn't derivable from the table — set it manually">
                                            {fmt(settings?.totals_incidence_rate)} <Pencil size={10} className="opacity-50" />
                                        </button>
                                    ) : fmt(settings?.totals_incidence_rate)}
                                </td>
                                <td className="px-3 py-2.5" data-testid="accident-stats-totals-fr">{fmt(totals.frequency_rate, 2)}</td>
                                <td></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}
            {clientId && rows.length > 0 && (
                <p className="text-[11.5px] text-[#9A968F] -mt-2">
                    Frequency Rate total is the sum of yearly rates. Incidence Rate total isn't derivable from the table columns —{" "}
                    {canEdit ? "click it above to set it manually." : "set by an editor."}
                </p>
            )}

            {clientId && rows.length > 0 && (
                <div className="rounded-xl border p-4 space-y-2" style={{ borderColor: "#E5E0DA" }} data-testid="accident-stats-benchmark">
                    <div className="flex items-center justify-between">
                        <h4 className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64]">Industry benchmark</h4>
                        {canEdit && !editingSettings && (
                            <button onClick={() => { setSettingsForm({ ...settings }); setEditingSettings(true); }}
                                data-testid="accident-stats-edit-benchmark"
                                className="text-[11.5px] font-semibold underline" style={{ color: BRAND }}>Edit</button>
                        )}
                    </div>
                    {editingSettings ? (
                        <div className="space-y-2">
                            <textarea data-testid="accident-stats-benchmark-note" rows={3} value={settingsForm.benchmark_note || ""}
                                onChange={(e) => setSettingsForm({ ...settingsForm, benchmark_note: e.target.value })}
                                className={inputCls + " h-auto py-2"} placeholder="Benchmark narrative shown on the printed report…" />
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                <input data-testid="accident-stats-benchmark-injury-lower" type="number" placeholder="Injury rate lower" value={settingsForm.benchmark_injury_lower ?? ""}
                                    onChange={(e) => setSettingsForm({ ...settingsForm, benchmark_injury_lower: e.target.value === "" ? null : parseFloat(e.target.value) })} className={inputCls} />
                                <input data-testid="accident-stats-benchmark-injury-upper" type="number" placeholder="Injury rate upper" value={settingsForm.benchmark_injury_upper ?? ""}
                                    onChange={(e) => setSettingsForm({ ...settingsForm, benchmark_injury_upper: e.target.value === "" ? null : parseFloat(e.target.value) })} className={inputCls} />
                                <input data-testid="accident-stats-benchmark-frequency-lower" type="number" placeholder="Frequency rate lower" value={settingsForm.benchmark_frequency_lower ?? ""}
                                    onChange={(e) => setSettingsForm({ ...settingsForm, benchmark_frequency_lower: e.target.value === "" ? null : parseFloat(e.target.value) })} className={inputCls} />
                                <input data-testid="accident-stats-benchmark-frequency-upper" type="number" placeholder="Frequency rate upper" value={settingsForm.benchmark_frequency_upper ?? ""}
                                    onChange={(e) => setSettingsForm({ ...settingsForm, benchmark_frequency_upper: e.target.value === "" ? null : parseFloat(e.target.value) })} className={inputCls} />
                            </div>
                            <div className="flex items-center gap-2">
                                <button onClick={saveSettings} data-testid="accident-stats-save-benchmark"
                                    className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-1.5 rounded-lg text-white" style={{ background: BRAND }}>
                                    <Save size={13} /> Save
                                </button>
                                <button onClick={() => setEditingSettings(false)} className="text-[12.5px] text-[#9A968F]">Cancel</button>
                            </div>
                        </div>
                    ) : (
                        <>
                            <p className="text-[12.5px] text-[#1F1F1F] whitespace-pre-line">{settings?.benchmark_note || "No benchmark narrative set yet."}</p>
                            {(settings?.benchmark_injury_lower != null || settings?.benchmark_frequency_lower != null) && (
                                <p className="text-[12px] text-[#6F6A64]">
                                    Construction Injury Rate 95% C.I. {fmt(settings?.benchmark_injury_lower)} – {fmt(settings?.benchmark_injury_upper)} ·{" "}
                                    Construction Frequency Rate 95% C.I. {fmt(settings?.benchmark_frequency_lower)} – {fmt(settings?.benchmark_frequency_upper)}
                                </p>
                            )}
                        </>
                    )}
                </div>
            )}

            {dialog && (
                <AccidentStatRowDialog row={dialog === "new" ? null : dialog}
                    existingYears={rows.map((r) => r.year_ending)}
                    onClose={() => setDialog(null)}
                    onSaved={() => { setDialog(null); refresh(); }}
                    clientId={clientId} />
            )}
        </div>
    );
}

function AccidentStatField({ label, children }) {
    return (
        <div><label className="block text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] mb-1">{label}</label>{children}</div>
    );
}

function AccidentStatRowDialog({ row, existingYears, onClose, onSaved, clientId }) {
    const isNew = !row;
    const [form, setForm] = useState(() => row ? { ...row } : {
        year_ending: new Date().getFullYear(), employees: 0, fatal: 0, reportable_injuries: 0,
        non_reportable: 0, dangerous_occurrences: 0, reportable_ill_health: 0, near_misses: 0,
        hours_worked: null, incidence_rate: null, frequency_rate: null,
    });
    const [saving, setSaving] = useState(false);
    const [pulling, setPulling] = useState(false);

    async function pullFromIncidents() {
        if (!form.year_ending) { toast.error("Set the year ending first"); return; }
        setPulling(true);
        try {
            const { data } = await api.get("/accident-stats/pull-from-incidents", { params: { client_id: clientId, year: form.year_ending } });
            setForm({
                ...form, fatal: data.fatal, reportable_injuries: data.reportable_injuries,
                non_reportable: data.non_reportable, dangerous_occurrences: data.dangerous_occurrences,
                reportable_ill_health: data.reportable_ill_health, near_misses: data.near_misses,
            });
            toast.success(data.matched_incidents > 0
                ? `Pulled from ${data.matched_incidents} incident(s) logged for ${form.year_ending} — adjust any figure manually if needed`
                : `No incidents logged for ${form.year_ending} yet — figures set to zero`);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not pull from the incident log"); }
        setPulling(false);
    }

    function autoCalc() {
        const injuries = (Number(form.fatal) || 0) + (Number(form.reportable_injuries) || 0);
        const employees = Number(form.employees) || 0;
        const hours = Number(form.hours_worked) || 0;
        const ir = employees > 0 ? Math.round((injuries / employees) * 100000) : null;
        const fr = hours > 0 ? Math.round((injuries / hours) * 1000000 * 100) / 100 : null;
        setForm({ ...form, incidence_rate: ir, frequency_rate: fr });
        toast.success("Calculated from the printed HSE formulas — you can still edit before saving");
    }

    async function save() {
        if (!form.year_ending) { toast.error("Year ending is required"); return; }
        if (isNew && existingYears.includes(Number(form.year_ending))) {
            toast.error(`A record for ${form.year_ending} already exists`); return;
        }
        setSaving(true);
        try {
            if (isNew) {
                await api.post("/accident-stats", { ...form, client_id: clientId, year_ending: Number(form.year_ending) });
                toast.success("Year added");
            } else {
                const payload = { ...form };
                delete payload.client_id; delete payload.id; delete payload.created_at;
                delete payload.created_by; delete payload.is_historic; delete payload.year_ending;
                delete payload.updated_at; delete payload.updated_by; delete payload.deleted_at;
                await api.patch(`/accident-stats/${row.id}`, payload);
                toast.success("Record updated");
            }
            onSaved();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
        setSaving(false);
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" data-testid="accident-stats-dialog">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col max-h-[92vh]">
                <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: "#E5E0DA" }}>
                    <h3 className="text-base font-bold text-[#1F1F1F]">{isNew ? "Add year" : `Year ending ${row.year_ending}`}</h3>
                    <button onClick={onClose} className="p-1 text-[#9A968F] hover:text-[#1F1F1F]"><X size={16} /></button>
                </div>
                <div className="p-5 space-y-3 overflow-y-auto">
                    {row?.is_historic && (
                        <div className="rounded-lg bg-[#F6F4F0] border p-2.5 text-[12px] text-[#6F6A64]" style={{ borderColor: "#E5E0DA" }}>
                            <History size={12} className="inline mr-1 -mt-0.5" /> Historic figures — preserved exactly as supplied. Edit only to correct a genuine data error.
                        </div>
                    )}
    <div className="grid grid-cols-2 gap-3">
                        <AccidentStatField label="Year ending *">
                            <input data-testid="accident-stats-input-year" type="number" disabled={!isNew} value={form.year_ending}
                                onChange={(e) => setForm({ ...form, year_ending: e.target.value })} className={inputCls} />
                        </AccidentStatField>
                        <div className="flex items-end">
                            {!row?.is_historic && (
                                <button type="button" onClick={pullFromIncidents} disabled={pulling} data-testid="accident-stats-pull-incidents"
                                    className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold h-9 px-2.5 rounded-lg disabled:opacity-50"
                                    style={{ border: "1px solid #E5E0DA", color: BRAND }}>
                                    {pulling ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Pull from Incident Log
                                </button>
                            )}
                        </div>
                        <AccidentStatField label="Employees">
                            <input data-testid="accident-stats-input-employees" type="number" min={0} value={form.employees}
                                onChange={(e) => setForm({ ...form, employees: e.target.value === "" ? 0 : parseInt(e.target.value, 10) })} className={inputCls} />
                        </AccidentStatField>
                        <AccidentStatField label="Fatal">
                            <input data-testid="accident-stats-input-fatal" type="number" min={0} value={form.fatal}
                                onChange={(e) => setForm({ ...form, fatal: e.target.value === "" ? 0 : parseInt(e.target.value, 10) })} className={inputCls} />
                        </AccidentStatField>
                        <AccidentStatField label="Reportable injuries">
                            <input data-testid="accident-stats-input-reportable-injuries" type="number" min={0} value={form.reportable_injuries}
                                onChange={(e) => setForm({ ...form, reportable_injuries: e.target.value === "" ? 0 : parseInt(e.target.value, 10) })} className={inputCls} />
                        </AccidentStatField>
                        <AccidentStatField label="Non-reportable">
                            <input data-testid="accident-stats-input-non-reportable" type="number" min={0} value={form.non_reportable}
                                onChange={(e) => setForm({ ...form, non_reportable: e.target.value === "" ? 0 : parseInt(e.target.value, 10) })} className={inputCls} />
                        </AccidentStatField>
                        <AccidentStatField label="Dangerous occurrences">
                            <input data-testid="accident-stats-input-dangerous-occurrences" type="number" min={0} value={form.dangerous_occurrences}
                                onChange={(e) => setForm({ ...form, dangerous_occurrences: e.target.value === "" ? 0 : parseInt(e.target.value, 10) })} className={inputCls} />
                        </AccidentStatField>
                        <AccidentStatField label="Reportable ill health">
                            <input data-testid="accident-stats-input-reportable-ill-health" type="number" min={0} value={form.reportable_ill_health}
                                onChange={(e) => setForm({ ...form, reportable_ill_health: e.target.value === "" ? 0 : parseInt(e.target.value, 10) })} className={inputCls} />
                        </AccidentStatField>
                        <AccidentStatField label="Near misses">
                            <input data-testid="accident-stats-input-near-misses" type="number" min={0} value={form.near_misses}
                                onChange={(e) => setForm({ ...form, near_misses: e.target.value === "" ? 0 : parseInt(e.target.value, 10) })} className={inputCls} />
                        </AccidentStatField>
                    </div>
                    <AccidentStatField label="Hours worked in year (supporting figure for Frequency Rate — not shown on the printed report)">
                        <input data-testid="accident-stats-input-hours-worked" type="number" min={0} value={form.hours_worked ?? ""}
                            onChange={(e) => setForm({ ...form, hours_worked: e.target.value === "" ? null : parseFloat(e.target.value) })} className={inputCls} />
                    </AccidentStatField>
                    <div className="flex items-center justify-between pt-1">
                        <h4 className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64]">Rates</h4>
                        <button type="button" onClick={autoCalc} data-testid="accident-stats-autocalc"
                            className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold" style={{ color: BRAND }}>
                            <Calculator size={12} /> Auto-calc from formula
                        </button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <AccidentStatField label="Incidence rate">
                            <input data-testid="accident-stats-input-incidence-rate" type="number" step="0.01" value={form.incidence_rate ?? ""}
                                onChange={(e) => setForm({ ...form, incidence_rate: e.target.value === "" ? null : parseFloat(e.target.value) })} className={inputCls} />
                        </AccidentStatField>
                        <AccidentStatField label="Frequency rate">
                            <input data-testid="accident-stats-input-frequency-rate" type="number" step="0.01" value={form.frequency_rate ?? ""}
                                onChange={(e) => setForm({ ...form, frequency_rate: e.target.value === "" ? null : parseFloat(e.target.value) })} className={inputCls} />
                        </AccidentStatField>
                    </div>
                </div>
                <div className="px-5 py-3.5 border-t flex justify-end gap-2" style={{ borderColor: "#E5E0DA" }}>
                    <button onClick={onClose} className="px-3.5 py-2 text-sm font-semibold text-[#6F6A64]">Cancel</button>
                    <button data-testid="accident-stats-save-row" onClick={save} disabled={saving}
                        className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-lg text-white disabled:opacity-60" style={{ background: BRAND }}>
                        {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save
                    </button>
                </div>
            </div>
        </div>
    );
}
